import type { OrganizationPublic, UserPublic } from '@garden-platform/shared';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import * as authService from '../services/auth';

interface AuthContextType {
  user: UserPublic | null;
  organization: OrganizationPublic | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (
    organizationName: string,
    email: string,
    password: string,
    name?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserPublic | null>(null);
  const [organization, setOrganization] = useState<OrganizationPublic | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data.user);
        setOrganization(response.data.organization || null);
      } else {
        setUser(null);
        setOrganization(null);
      }
    } catch (_error) {
      setUser(null);
      setOrganization(null);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      await refreshUser();
      setIsLoading(false);
    };
    initAuth();
  }, [refreshUser]);

  const login = async (
    email: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authService.login({ email, password });
      if (response.success && response.data) {
        setUser(response.data.user);
        setOrganization(response.data.organization || null);
        return { success: true };
      }
      return { success: false, error: response.error || 'Login failed' };
    } catch (_error) {
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const register = async (
    organizationName: string,
    email: string,
    password: string,
    name?: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authService.register({
        organizationName,
        email,
        password,
        name,
      });
      if (response.success && response.data) {
        setUser(response.data.user);
        setOrganization(response.data.organization || null);
        return { success: true };
      }
      return { success: false, error: response.error || 'Registration failed' };
    } catch (_error) {
      return { success: false, error: 'Registration failed. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setOrganization(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
